define((require) => {
    "use strict";

    const PublishableState = require("util/PublishableState");
    const GroupModel       = require("shared/ui/groups/GroupModel");
    const ExperienceModel  = require("shared/ui/experience/ExperienceModel");
    const GroupUtil        = require("ui/groups/GroupUtil");
    const Locale           = require("shared/services/localization/Locale");

    // Common publishable attributes we must clear on clone or update on sync, true for any publishable
    const ATTRIBUTES = ["state", "pushedAt", "pushedBy", "pushedVersion", "purgeTime", "editedAt", "editedBy"];

    /**
     * A mixin adding Publishable behaviour to models.
     */
    return {

        __typeName: "PublishableMixin", // for better type naming

        // Defaults to be mixed in for any Publishable type...
        defaults: {
            state:           null, // string, not PublishableState constant
            pushedAt:        0,
            pushedBy:        null,
            pushedVersion:   null,
            purgeTime:       null,
            editedAt:        0,  // by definition, there cannot be a default edited time!
            editedBy:        null
        },

        // Clear these fields on duplicate, i.e. when we duplicate to save a new copy...
        clearOnClone(options) {
            let attributes = this.__super();
            if (options.duplicate) {
                attributes = attributes ? attributes.concat(ATTRIBUTES) : ATTRIBUTES;
            }
            return attributes;
        },

        // Update these attributes after the specified sync method has been performed on this model; common meta data
        // attributes will always be updated where applicable, e.g. "id", "version", "createdBy", etc...
        updateOnSync(options) {
            let attributes = this.__super();
            if (options.sync === "save") {
                if (attributes) {
                    attributes.push("state");
                } else {
                    attributes = ["state"];
                }
            } else if (options.sync.indexOf("push") !== -1) { // for "push" and "push-with-assets" when pushed as a group asset
                attributes = attributes ? attributes.concat(ATTRIBUTES) : ATTRIBUTES;
            }
            return attributes;
        },

        // Will be mixed in with existing transient properties...
        transient: ["purgeTime", "state"],

        /**
         * Returns the <tt>PublishableState</tt> constant for this publishable, defaulting to
         * <tt>NOT_PUSHED</tt> if no state currently set.
         *
         * @return {PublishableState} The <tt>PublishableState</tt> constant for this publishable; never falsey,
         *                            defaulting to <tt>NOT_PUSHED</tt>.
         */
        getState() {
            return PublishableState.getEnum(this.get("state"));
        },

        /**
         * Returns true if this publishable has at least the specified state, false otherwise.
         *
         * @param  {Object}  state The state, as the enum representation, e.g. PublishableState.PUSHED, or as
         *                         its key, e.g. "PUSHED".
         * @return {Boolean} <tt>true</tt> if this publishable has at least the specified state, false otherwise.
         */
        hasAtLeastState(state) {
            return PublishableState.atLeast(this.get("state"), state);
        },

        /**
         * Returns the max purge time between this publishable model and the supplied purge time, if any. <p>
         *
         * If <tt>purgeTime</tt> is not specified, the purge time of this publishable will be returned, defaulting
         * to 0 if not set.
         *
         * @param {Number}  [purgeTime] The optional purge time to test against the purge time of this publishable;
         *                              can be falsey.
         * @return {Number} The (max) purge time for this publishable and <tt>purgeTime</tt>; can be null if not
         *                  applicable.
         */
        getPurgeTime(purgeTime) {
            if (this.has("purgeTime")) {
                return Math.max(purgeTime || 0, this.get("purgeTime"));
            }
            return purgeTime;  // can be null!
        },

        /**
         * Returns true if this publishable has changes since last push, false otherwise.
         *
         * @return {Boolean} True if changes since last push, false otherwise.
         * @see    #nonPushedChanges
         */
        isChangedAfterPush() {
            // abs to ensure that stale pushed data will also work (negative versions to ensure we cannot
            // ever save such stale data again)...
            return this.hasAtLeastState(PublishableState.PUSHED) && (Math.abs(this.get("version")) > this.get("pushedVersion"));
        },

        /**
         * Returns true if this publishable has changes that are not pushed, false otherwise.
         *
         * @return {Boolean} True if non-pushed changes, false otherwise.
         * @see    #isChangedAfterPush
         */
        hasNonPushedChanges() {
            return this.getState() === PublishableState.NOT_PUSHED || this.isChangedAfterPush();
        },

        /**
         * Returns true if this publishable is an experience.
         *
         * @returns {Boolean} True if the publishable is an experience.
         */
        isExperience() {
            return (this instanceof ExperienceModel || this.get("type") === "Experience") && !this.get("group");
        },

        /**
         * Returns true if this publishable is a localized experience.
         *
         * @returns {Boolean} True if the publishable is a localized experience.
         */
        isLocalizedExperience() {
            const group = this.get("group");
            return (this instanceof ExperienceModel || this.get("type") === "Experience") &&
                    group && group.type === GroupUtil.LOCALIZATION; // Embedded group is a plain object
        },

        /**
         * Returns true if this publishable is a breakpoint group.
         *
         * @returns {Boolean} True if the publishable is a breakpoint group.
         */
        isBreakpointGroup() {
            return this instanceof GroupModel || this.get("type") === GroupUtil.BREAKPOINT;
        },


        /**
         * Returns the publishable display name, mainly to handle localized experiences name.
         *
         * @returns {String} The publishable name.
         */
        publishableDisplayName() {
            const displayName = this.get("name");

            if (this.isBreakpointGroup()) {
                return displayName;
            }

            if (this.isLocalizedExperience()) {
                const locale = Locale.localeStringFromModel(this);
                return `${displayName} ${locale}`;
            }

            return displayName;
        }

    };
});



// WEBPACK FOOTER //
// ./src/editor/util/PublishableMixin.js